import { CAST_AI } from '@cast/constants';
import { Box, Divider, Stack, Typography, tokens } from '@cast/design-system';
import { useConsoleAccess, useReportingTrial } from '@cast/shared-trial-ui';

import { CastctlConnectMethodTabs } from '@oneclick/features/connect-cluster/_components/CastctlConnectMethodTabs/CastctlConnectMethodTabs';
import { getCastctlFeatureSelectionDefaults } from 'hooks/dialogs/cluster/components/CastctlOnboardingDialog';

import { Permissions } from './_components/Permissions';
import { OnScriptStateChange } from '../../../_constants';

const PERMISSIONS = [
  'kubectl access to the target cluster',
  `Helm chart installs ${CAST_AI} agent with minimal RBAC`,
];

type SingleProps = {
  onScriptStateChange?: OnScriptStateChange;
  isDialogLayout?: boolean;
};

export const Single = ({
  onScriptStateChange,
  isDialogLayout = false,
}: SingleProps) => {
  const { isLoading: isReportingLoading } = useReportingTrial();
  const { limitAutomationConsole } = useConsoleAccess();
  // Reporting-trial eligible orgs keep REP-2386 cost-monitoring-only
  // behaviour (automation flags explicitly off). For all other orgs, and
  // while reporting status loads, pass `undefined` so the rendered command
  // is the bare `castctl cluster connect` (api-token / api-region /
  // organization-id only) and castctl decides which features to install.
  const isReportingTrialLimited = !isReportingLoading && limitAutomationConsole;

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: '100%',
        pl: 20,
        pr: 24,
        pt: 24,
        pb: isDialogLayout ? tokens.primitives.space[600] : 0,
        gap: 0,
      }}
    >
      <CastctlConnectMethodTabs
        featureSelection={
          isReportingTrialLimited
            ? getCastctlFeatureSelectionDefaults(true)
            : undefined
        }
        castctlScriptTestId="castctl-connect-command"
        onScriptStateChange={onScriptStateChange}
      />

      {/* Align with the tabs' inner content (badge + gap = 40px from the
          stack's content edge) — the previous `pl: 8` put the disclaimer
          far to the left of the snippet, so it looked misaligned. */}
      <Box sx={{ pl: 42, pb: 16, pt: 16 }}>
        <Divider />
      </Box>

      <Typography
        variant="P12R"
        color={tokens.theme.fg.structural.secondary}
        sx={{ pl: 42 }}
      >
        This installs the {CAST_AI} read-only agent. Your sensitive data remains
        secure and your cluster configuration unchanged.
      </Typography>

      <Permissions permissions={PERMISSIONS} />
    </Stack>
  );
};
